import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaExchangeAlt, 
  FaShieldAlt, 
  FaCog, 
  FaUsers, 
  FaGift, 
  FaRocket,
  FaExternalLinkAlt,
  FaEye,
  FaTimes
} from 'react-icons/fa';
import { formatDate } from '../utils/formatters';

// Fonction pour déterminer l'icône et les couleurs en fonction du type de notification
const getNotificationIcon = (type, priority = 'medium') => {
  const baseClasses = "flex items-center justify-center w-12 h-12 rounded-xl";
  
  const priorityRing = priority === 'high' 
    ? 'ring-2 ring-red-400/50' 
    : priority === 'medium' 
    ? 'ring-1 ring-blue-400/30' 
    : '';

  switch (type) {
    case "transaction":
      return (
        <div className={`${baseClasses} bg-gradient-to-br from-green-500/20 to-emerald-600/20 text-green-400 ${priorityRing}`}>
          <FaExchangeAlt className="w-6 h-6" />
        </div>
      );
    case "security":
      return (
        <div className={`${baseClasses} bg-gradient-to-br from-red-500/20 to-rose-600/20 text-red-400 ${priorityRing}`}>
          <FaShieldAlt className="w-6 h-6" />
        </div>
      );
    case "system":
      return (
        <div className={`${baseClasses} bg-gradient-to-br from-blue-500/20 to-indigo-600/20 text-blue-400 ${priorityRing}`}>
          <FaCog className="w-6 h-6" />
        </div>
      );
    case "bbt":
      return (
        <div className={`${baseClasses} bg-gradient-to-br from-amber-500/20 to-orange-600/20 text-amber-400 ${priorityRing}`}>
          <FaRocket className="w-6 h-6" />
        </div>
      );
    case "affiliate":
      return (
        <div className={`${baseClasses} bg-gradient-to-br from-purple-500/20 to-violet-600/20 text-purple-400 ${priorityRing}`}>
          <FaUsers className="w-6 h-6" />
        </div>
      );
    case "promotion":
      return (
        <div className={`${baseClasses} bg-gradient-to-br from-pink-500/20 to-rose-600/20 text-pink-400 ${priorityRing}`}>
          <FaGift className="w-6 h-6" />
        </div>
      );
    default:
      return (
        <div className={`${baseClasses} bg-gradient-to-br from-gray-500/20 to-slate-600/20 text-gray-400 ${priorityRing}`}>
          <FaEye className="w-6 h-6" />
        </div>
      );
  }
};

const getPriorityIndicator = (priority) => {
  switch (priority) {
    case 'high':
      return <div className="w-1 h-full bg-gradient-to-b from-red-400 to-red-600 rounded-full" />;
    case 'medium':
      return <div className="w-1 h-full bg-gradient-to-b from-yellow-400 to-orange-500 rounded-full" />;
    case 'low':
      return <div className="w-1 h-full bg-gradient-to-b from-green-400 to-green-600 rounded-full" />;
    default:
      return null;
  }
};

const NotificationItem = React.memo(({ notification, onClick, onAction, index }) => {
  const handleMainClick = (e) => {
    e.preventDefault();
    onClick(notification.id);
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    onAction(notification.id, action);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20, transition: { duration: 0.2 } }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`relative flex items-start gap-4 p-4 hover:bg-slate-700/50 cursor-pointer transition-all duration-200 group ${
        !notification.read 
          ? "bg-slate-700/30 border-l-4 border-blue-500" 
          : "bg-slate-800/20 hover:bg-slate-700/30"
      }`}
      onClick={handleMainClick}
      role="listitem"
      aria-live="polite"
    >
      {/* Priority Indicator */}
      <div className="absolute left-0 top-0 bottom-0 w-1">
        {getPriorityIndicator(notification.priority)}
      </div>

      {/* Icon */}
      <div className="flex-shrink-0 relative">
        {getNotificationIcon(notification.type, notification.priority)}
        {!notification.read && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-slate-800" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-semibold text-white leading-tight group-hover:text-blue-300 transition-colors">
            {notification.title}
          </h4>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-slate-400 whitespace-nowrap">
              {formatDate(notification.date)}
            </span>
            {!notification.read && (
              <div className="w-2 h-2 bg-blue-500 rounded-full" title="Non lue" />
            )}
          </div>
        </div>

        {/* Message */}
        <p className="text-sm text-slate-300 leading-relaxed">
          {notification.message}
        </p>

        {/* Actions */}
        {notification.actions && notification.actions.length > 0 && (
          <div className="flex items-center gap-2 pt-2">
            {notification.actions.map((action, actionIndex) => (
              <button
                key={actionIndex}
                onClick={(e) => handleActionClick(e, action.action)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 flex items-center gap-1.5 ${
                  action.primary
                    ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
                    : 'bg-slate-600 hover:bg-slate-500 text-slate-200'
                }`}
              >
                {action.action === 'view_transaction' && <FaExternalLinkAlt className="w-3 h-3" />}
                {action.action === 'secure_account' && <FaShieldAlt className="w-3 h-3" />}
                {action.action === 'trade_bbt' && <FaRocket className="w-3 h-3" />}
                {action.action === 'view_earnings' && <FaUsers className="w-3 h-3" />}
                {action.action === 'use_promo' && <FaGift className="w-3 h-3" />}
                {action.action === 'ignore' && <FaTimes className="w-3 h-3" />}
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Priority Badge */}
        {notification.priority === 'high' && (
          <div className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
            <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
            Urgent
          </div>
        )}
      </div>

      {/* Hover Effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </motion.div>
  );
});

NotificationItem.displayName = 'NotificationItem';

export default NotificationItem; 
